export default () => {
	const app = useNuxtApp()
	const mounted = useMounted()
	const runtimeConfig = useRuntimeConfig()

	const ntfyToken = useCookie<string | null>("ntfy_token", {
		maxAge: 60 * 60 * 24 * 30 * 2
	})

	const ntfyTopic = useCookie<string | null>("ntfy_topic", {
		maxAge: 60 * 60 * 24 * 30 * 2
	})

	const checkAndReRegister = async (registration: ServiceWorkerRegistration) => {
		// 1. Check for existing subscription in the browser's push manager
		const existingSubscription = await registration.pushManager.getSubscription()

		if (existingSubscription) {
			// 2. Check if the subscription was created with the current VAPID key
			const currentVapidKey = app.$config.public.ntfyVapidPublicKey
			if (currentVapidKey) {
				try {
					// Get the application server key (VAPID public key) from the subscription
					const subscriptionOptions = existingSubscription.options
					const subscriptionKey = subscriptionOptions?.applicationServerKey

					if (subscriptionKey) {
						// Convert both keys to base64 strings for comparison
						const currentKeyBuffer = urlBase64ToUint8Array(currentVapidKey)
						const subscriptionKeyArray = new Uint8Array(subscriptionKey)

						// Compare the keys
						const keysMatch =
							currentKeyBuffer.length === subscriptionKeyArray.length &&
							currentKeyBuffer.every((value, index) => value === subscriptionKeyArray[index])

						if (!keysMatch) {
							console.warn(
								"[ntfy] VAPID key mismatch detected! Current key differs from subscription key."
							)

							// Unsubscribe from server first
							try {
								await $fetch(runtimeConfig.public.baseUrl + "push/unsubscribe", {
									method: "POST",
									body: existingSubscription.toJSON()
								})
							} catch (e) {
								console.warn("[ntfy] Failed to unsubscribe from server (may not exist):", e)
							}

							// Unsubscribe from browser
							await existingSubscription.unsubscribe()

							// Return false to indicate we need to create a new subscription
							return false
						}
					}
				} catch (e) {
					console.error("[ntfy] Error validating VAPID key:", e)
					// Continue with re-registration anyway
				}
			}

			// 3. VAPID key matches or validation failed - re-register with server
			try {
				await $fetch(runtimeConfig.public.baseUrl + "push/subscribe", {
					method: "POST",
					body: {
						subscription: existingSubscription.toJSON(),
						topic: ntfyTopic.value,
						token: ntfyToken.value,
						// Tell backend to clean up any other subscriptions for this user
						// This helps remove duplicates from before the fix
						cleanup_duplicates: true
					}
				})
				return true
			} catch (e) {
				console.error("[ntfy] Re-registration error:", e)
				app.$toast.error({ message: "Failed to subscribe to notifications" })
				return false
			}
		}

		return false
	}

	// Helper function to convert URL-safe base64 VAPID key to Uint8Array
	const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
		const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
		const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
		const rawData = window.atob(base64)
		const outputArray = new Uint8Array(rawData.length)
		for (let i = 0; i < rawData.length; ++i) {
			outputArray[i] = rawData.charCodeAt(i)
		}
		return outputArray
	}

	const sub = async (skipPermissionRequest: boolean = false) => {
		// Check if service worker and push manager are supported
		if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
			console.error("[ntfy] Service worker or push notifications not supported")
			return false
		}

		// 1. Get Service Worker Registration with timeout
		let registration: ServiceWorkerRegistration
		try {
			registration = await Promise.race([
				navigator.serviceWorker.ready,
				new Promise<never>((_, reject) =>
					setTimeout(() => reject(new Error("Service worker timeout after 5 seconds")), 5000)
				)
			])
		} catch (e) {
			console.error("[ntfy] Service worker not ready:", e)
			return false
		}

		// 2. Check and handle existing subscriptions
		const subscriptionHandled = await checkAndReRegister(registration)
		if (subscriptionHandled) {
			return true
		}

		// If we reach here, no existing subscription was found
		// Check if there's an old subscription we should clean up first
		const existingSubscription = await registration.pushManager.getSubscription()
		if (existingSubscription) {
			await existingSubscription.unsubscribe()
		}

		// 3. Check current permission status
		const currentPermission = Notification.permission

		// If permission is denied, we can't do anything
		if (currentPermission === "denied") {
			return false
		}

		// If permission is not granted and we should skip asking, return false
		if (currentPermission !== "granted" && skipPermissionRequest) {
			return false
		}

		// 4. Request Notification Permission only if not already granted
		let permission: NotificationPermission = currentPermission
		if (currentPermission === "default") {
			permission = await Notification.requestPermission()
		}

		if (permission !== "granted") {
			app.$toast.error({ message: app.$i18n.t("notification_permission_denied") })
			return false
		}

		try {
			// 5. Subscribe to Web Push using VAPID public key
			const subscription = await registration.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: app.$config.public.ntfyVapidPublicKey
			})

			// 6. Send subscription to the Nuxt push API with topic and token
			await $fetch(runtimeConfig.public.baseUrl + "push/subscribe", {
				method: "POST",
				body: {
					subscription: subscription.toJSON(),
					topic: ntfyTopic.value,
					token: ntfyToken.value,
					// Tell backend to clean up any other subscriptions for this user
					cleanup_duplicates: true
				}
			})

			console.log("[ntfy] Push notifications enabled")
			return true
		} catch (e) {
			console.error("[ntfy] Push subscription error:", e)
			app.$toast.error({ message: "Failed to subscribe to notifications" })
			return false
		}
	}

	const unsub = async () => {
		if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
			console.error("[ntfy] Service worker or push notifications not supported")
			return false
		}

		try {
			const registration = await navigator.serviceWorker.ready
			const subscription = await registration.pushManager.getSubscription()

			if (subscription) {
				// Unsubscribe from server
				await $fetch(runtimeConfig.public.baseUrl + "push/unsubscribe", {
					method: "POST",
					body: subscription.toJSON()
				})

				// Unsubscribe from browser
				await subscription.unsubscribe()

				console.log("[ntfy] Push notifications disabled")
				return true
			} else {
				return false
			}
		} catch (e) {
			console.error("[ntfy] Unsubscribe error:", e)
			return false
		}
	}

	// Track if we've synced on initial mount
	const hasInitialSync = ref(false)

	watchDebounced(
		() => [ntfyToken.value, ntfyTopic.value, mounted.value],
		async (n, o) => {
			// Early exit if VAPID key not configured - push notifications disabled globally
			if (!app.$config.public.ntfyVapidPublicKey) {
				return
			}

			// Early exit if not mounted yet
			if (!mounted.value) {
				return
			}

			// On initial mount with token/topic, always sync even if values haven't changed
			// This handles case where user clears cookies but subscription still exists in browser
			const isInitialMount = !hasInitialSync.value && ntfyToken.value && ntfyTopic.value

			// Skip if values haven't changed AND we're not on initial mount
			if (!isInitialMount && o && n[0] === o[0] && n[1] === o[1] && n[2] === o[2]) {
				return
			}

			// If token or topic are cleared, unsubscribe
			if (!ntfyToken.value || !ntfyTopic.value) {
				if (o && (o[0] || o[1])) {
					// Had previous values, now cleared - unsubscribe
					await unsub()
				}
				hasInitialSync.value = false
				return
			}

			// Have both token and topic, subscribe
			// Automatically ask for permission if not yet decided (default state)
			// If denied, user can enable it manually via settings
			await sub(false)
			hasInitialSync.value = true
		},
		{ immediate: true, debounce: 500 }
	)

	const getPermissionStatus = () => {
		if (!("Notification" in window)) {
			return "unsupported"
		}
		return Notification.permission
	}

	const isSubscribed = async () => {
		if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
			return false
		}

		try {
			const registration = await navigator.serviceWorker.ready
			const subscription = await registration.pushManager.getSubscription()
			return !!subscription
		} catch (e) {
			console.error("[ntfy] Error checking subscription:", e)
			return false
		}
	}

	const requestPermissionAndSubscribe = async () => {
		if (!ntfyToken.value || !ntfyTopic.value) {
			return false
		}
		return await sub(false)
	}

	// Debug utility to check current subscription
	const debugSubscription = async () => {
		if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
			return null
		}

		try {
			const registration = await navigator.serviceWorker.ready
			const subscription = await registration.pushManager.getSubscription()

			if (subscription) {
				return subscription.toJSON()
			} else {
				return null
			}
		} catch (e) {
			console.error("[ntfy] Error checking subscription:", e)
			return null
		}
	}

	return {
		ntfyToken,
		ntfyTopic,
		set: (topic: string | null, token: string | null) => {
			ntfyTopic.value = topic
			ntfyToken.value = token
		},
		getPermissionStatus,
		isSubscribed,
		requestPermissionAndSubscribe,
		unsubscribe: unsub,
		debugSubscription
	}
}
